// Technical Analysis Indicators Service
export interface PriceData {
  timestamp: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface IndicatorResult {
  value: number;
  timestamp: number;
  signal?: 'buy' | 'sell' | 'neutral';
  strength?: number; // 0-100
}

export interface RSIData extends IndicatorResult {
  overbought: boolean; // >70
  oversold: boolean; // <30
}

export interface MACDData {
  macd: number;
  signal: number;
  histogram: number;
  timestamp: number;
  signal_type?: 'buy' | 'sell' | 'neutral';
}

export interface BollingerBandsData {
  upper: number;
  middle: number; // SMA
  lower: number;
  timestamp: number;
  position?: 'above_upper' | 'below_lower' | 'between';
}

export interface MovingAverageData extends IndicatorResult {
  type: 'SMA' | 'EMA';
  period: number;
}

export interface VolumeData {
  volume: number;
  avgVolume: number;
  volumeRatio: number;
  timestamp: number;
  signal?: 'high' | 'normal' | 'low';
}

export class TechnicalAnalysisService {
  // RSI (Relative Strength Index)
  public calculateRSI(prices: number[], period: number = 14): RSIData[] {
    const rsiData: RSIData[] = [];
    
    for (let i = period; i < prices.length; i++) {
      const gains: number[] = [];
      const losses: number[] = [];
      
      for (let j = i - period + 1; j <= i; j++) {
        const change = prices[j] - prices[j - 1];
        gains.push(change > 0 ? change : 0);
        losses.push(change < 0 ? Math.abs(change) : 0);
      }
      
      const avgGain = gains.reduce((sum, gain) => sum + gain, 0) / period;
      const avgLoss = losses.reduce((sum, loss) => sum + loss, 0) / period;
      
      const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
      const rsi = 100 - (100 / (1 + rs));
      
      const overbought = rsi > 70;
      const oversold = rsi < 30;
      
      let signal: 'buy' | 'sell' | 'neutral' = 'neutral';
      if (oversold) signal = 'buy';
      else if (overbought) signal = 'sell';
      
      rsiData.push({
        value: rsi,
        timestamp: Date.now() - (prices.length - i) * 60000, // 1 min intervals
        signal,
        strength: Math.abs(rsi - 50) * 2, // 0-100
        overbought,
        oversold
      });
    }
    
    return rsiData;
  }

  // MACD (Moving Average Convergence Divergence)
  public calculateMACD(prices: number[], fastPeriod: number = 12, slowPeriod: number = 26, signalPeriod: number = 9): MACDData[] {
    const emaFast = this.calculateEMA(prices, fastPeriod);
    const emaSlow = this.calculateEMA(prices, slowPeriod);
    
    const macdLine: number[] = [];
    for (let i = 0; i < Math.min(emaFast.length, emaSlow.length); i++) {
      macdLine.push(emaFast[i] - emaSlow[i]);
    }
    
    const signalLine = this.calculateEMA(macdLine, signalPeriod);
    const histogram: number[] = [];
    
    for (let i = 0; i < Math.min(macdLine.length, signalLine.length); i++) {
      histogram.push(macdLine[i] - signalLine[i]);
    }
    
    const macdData: MACDData[] = [];
    const startIndex = Math.max(slowPeriod - 1, signalPeriod - 1);
    
    for (let i = startIndex; i < macdLine.length; i++) {
      const macd = macdLine[i];
      const signal = signalLine[i - startIndex] || 0;
      const hist = histogram[i - startIndex] || 0;
      
      let signal_type: 'buy' | 'sell' | 'neutral' = 'neutral';
      if (macd > signal && hist > 0) signal_type = 'buy';
      else if (macd < signal && hist < 0) signal_type = 'sell';
      
      macdData.push({
        macd,
        signal,
        histogram: hist,
        timestamp: Date.now() - (macdLine.length - i) * 60000,
        signal_type
      });
    }
    
    return macdData;
  }

  // Bollinger Bands
  public calculateBollingerBands(prices: number[], period: number = 20, stdDev: number = 2): BollingerBandsData[] {
    const bbData: BollingerBandsData[] = [];
    
    for (let i = period - 1; i < prices.length; i++) {
      const slice = prices.slice(i - period + 1, i + 1);
      const sma = slice.reduce((sum, price) => sum + price, 0) / period;
      
      const variance = slice.reduce((sum, price) => sum + Math.pow(price - sma, 2), 0) / period;
      const standardDeviation = Math.sqrt(variance);
      
      const upper = sma + (stdDev * standardDeviation);
      const lower = sma - (stdDev * standardDeviation);
      
      const currentPrice = prices[i];
      let position: 'above_upper' | 'below_lower' | 'between' = 'between';
      if (currentPrice > upper) position = 'above_upper';
      else if (currentPrice < lower) position = 'below_lower';
      
      bbData.push({
        upper,
        middle: sma,
        lower,
        timestamp: Date.now() - (prices.length - i) * 60000,
        position
      });
    }
    
    return bbData;
  }

  // Simple Moving Average (SMA)
  public calculateSMA(prices: number[], period: number): MovingAverageData[] {
    const smaData: MovingAverageData[] = [];
    
    for (let i = period - 1; i < prices.length; i++) {
      const slice = prices.slice(i - period + 1, i + 1);
      const sma = slice.reduce((sum, price) => sum + price, 0) / period;
      
      smaData.push({
        value: sma,
        timestamp: Date.now() - (prices.length - i) * 60000,
        type: 'SMA',
        period,
        signal: 'neutral',
        strength: 0
      });
    }
    
    return smaData;
  }

  // Exponential Moving Average (EMA)
  public calculateEMA(prices: number[], period: number): number[] {
    const ema: number[] = [];
    const multiplier = 2 / (period + 1);
    
    // Start with SMA for the first value
    const sma = prices.slice(0, period).reduce((sum, price) => sum + price, 0) / period;
    ema.push(sma);
    
    for (let i = period; i < prices.length; i++) {
      const currentEMA = (prices[i] * multiplier) + (ema[ema.length - 1] * (1 - multiplier));
      ema.push(currentEMA);
    }
    
    return ema;
  }

  // Volume Analysis
  public calculateVolumeAnalysis(volumes: number[], period: number = 20): VolumeData[] {
    const volumeData: VolumeData[] = [];
    
    for (let i = period - 1; i < volumes.length; i++) {
      const slice = volumes.slice(i - period + 1, i + 1);
      const avgVolume = slice.reduce((sum, vol) => sum + vol, 0) / period;
      const currentVolume = volumes[i];
      const volumeRatio = avgVolume > 0 ? currentVolume / avgVolume : 1;
      
      let signal: 'high' | 'normal' | 'low' = 'normal';
      if (volumeRatio > 1.5) signal = 'high';
      else if (volumeRatio < 0.5) signal = 'low';
      
      volumeData.push({
        volume: currentVolume,
        avgVolume,
        volumeRatio,
        timestamp: Date.now() - (volumes.length - i) * 60000,
        signal
      });
    }
    
    return volumeData;
  }

  // Generate sample price data for testing
  public generateSampleData(periods: number = 100): PriceData[] {
    const data: PriceData[] = [];
    let price = 50000; // Starting price
    
    for (let i = 0; i < periods; i++) {
      const change = (Math.random() - 0.5) * 1000; // Random price change
      price += change;
      
      const high = price + Math.random() * 200;
      const low = price - Math.random() * 200;
      const open = i === 0 ? price : data[i - 1].close;
      const close = price;
      const volume = Math.random() * 1000000;
      
      data.push({
        timestamp: Date.now() - (periods - i) * 60000,
        open,
        high,
        low,
        close,
        volume
      });
    }
    
    return data;
  }

  // Fetch real-time price data from APIs
  public async fetchRealTimeData(symbol: string): Promise<PriceData[]> {
    try {
      // Try multiple sources for real-time data
      const [binanceData, coinGeckoData] = await Promise.allSettled([
        this.fetchBinanceData(symbol),
        this.fetchCoinGeckoData(symbol)
      ]);

      // Use Binance data if available, fallback to CoinGecko
      if (binanceData.status === 'fulfilled' && binanceData.value.length > 0) {
        return binanceData.value;
      } else if (coinGeckoData.status === 'fulfilled' && coinGeckoData.value.length > 0) {
        return coinGeckoData.value;
      }

      // Fallback to sample data if APIs fail
      console.warn('Real-time data unavailable, using sample data');
      return this.generateSampleData(100);
    } catch (error) {
      console.warn('Failed to fetch real-time data:', error);
      return this.generateSampleData(100);
    }
  }

  // Fetch data from Binance API
  private async fetchBinanceData(symbol: string): Promise<PriceData[]> {
    try {
      const response = await fetch(`https://api.binance.com/api/v3/klines?symbol=${symbol}&interval=1m&limit=100`);
      const data = await response.json();
      
      return data.map((kline: any) => ({
        timestamp: kline[0],
        open: parseFloat(kline[1]),
        high: parseFloat(kline[2]),
        low: parseFloat(kline[3]),
        close: parseFloat(kline[4]),
        volume: parseFloat(kline[5])
      }));
    } catch (error) {
      console.warn('Failed to fetch Binance data:', error);
      return [];
    }
  }

  // Fetch data from CoinGecko API
  private async fetchCoinGeckoData(symbol: string): Promise<PriceData[]> {
    try {
      // Map symbol to CoinGecko ID
      const symbolMap: { [key: string]: string } = {
        'BTCUSDT': 'bitcoin',
        'ETHUSDT': 'ethereum',
        'SOLUSDT': 'solana',
        'ADAUSDT': 'cardano',
        'BNBUSDT': 'binancecoin',
        'XRPUSDT': 'ripple',
        'DOGEUSDT': 'dogecoin',
        'AVAXUSDT': 'avalanche-2',
        'DOTUSDT': 'polkadot',
        'LINKUSDT': 'chainlink'
      };

      const coinId = symbolMap[symbol] || 'bitcoin';
      const response = await fetch(`https://api.coingecko.com/api/v3/coins/${coinId}/market_chart?vs_currency=usd&days=1&interval=hourly`);
      const data = await response.json();
      
      const prices = data.prices || [];
      const volumes = data.total_volumes || [];
      
      return prices.slice(-100).map((priceData: any, index: number) => ({
        timestamp: priceData[0],
        open: priceData[1],
        high: priceData[1] * (1 + Math.random() * 0.02), // Approximate high
        low: priceData[1] * (1 - Math.random() * 0.02),  // Approximate low
        close: priceData[1],
        volume: volumes[index] ? volumes[index][1] : Math.random() * 1000000
      }));
    } catch (error) {
      console.warn('Failed to fetch CoinGecko data:', error);
      return [];
    }
  }

  // Get all indicators for a symbol with real-time data
  public async getAllIndicators(symbol: string): Promise<{
    rsi: RSIData[];
    macd: MACDData[];
    bollinger: BollingerBandsData[];
    sma20: MovingAverageData[];
    sma50: MovingAverageData[];
    ema12: MovingAverageData[];
    ema26: MovingAverageData[];
    volume: VolumeData[];
  }> {
    // Fetch real-time data
    const realTimeData = await this.fetchRealTimeData(symbol);
    const prices = realTimeData.map(d => d.close);
    const volumes = realTimeData.map(d => d.volume);
    
    return {
      rsi: this.calculateRSI(prices),
      macd: this.calculateMACD(prices),
      bollinger: this.calculateBollingerBands(prices),
      sma20: this.calculateSMA(prices, 20),
      sma50: this.calculateSMA(prices, 50),
      ema12: this.calculateSMA(prices, 12).map(d => ({ ...d, type: 'EMA' as const })),
      ema26: this.calculateSMA(prices, 26).map(d => ({ ...d, type: 'EMA' as const })),
      volume: this.calculateVolumeAnalysis(volumes)
    };
  }
}

export const technicalAnalysisService = new TechnicalAnalysisService();
